import {
  BaseChartDataItem,
  FinancesServicesBreakdown,
  FinancesVGrubsProviderRow,
  GroupByType,
  lightTheme,
} from '@vgrubs/components';
import { BreakdownItem, FinanceTabbedCard } from './FinanceTabbedCard';
import {
  SERVICE_COLORS,
  SERVICE_FEE_LABEL,
  SERVICE_FEE_TOOLTIP,
  VDRIVE_NEGATIVE_FEE_LABEL,
  VDRIVE_NEGATIVE_FEE_TOOLTIP,
} from './serviceLegend';
import { buildVGrubsProviderRows } from './vGrubsProviderRows';

// Same ordering as the original pie legend.
const SERVICE_LABELS: {
  key: keyof FinancesServicesBreakdown;
  label: string;
  tooltip?: string;
}[] = [
  {
    key: 'vGrubs',
    label: 'vGrubs VR',
    // The line is everything vGrubs keeps out of the provider payout, not a
    // single fee - the per-provider drill-in splits it into these same three
    // parts. Marketplace fee stopped being a service of its own and lives in
    // here now, so the card no longer lists it next to this row.
    tooltip:
      'What vGrubs keeps from the provider payout: vGrubs fee, sales tax and marketplace override fee.',
  },
  { key: 'vOrders', label: 'vOrders' },
  { key: 'saas', label: 'Monthly Saas' },
  { key: 'vDrive', label: 'vDrive' },
  {
    key: 'vDriveNegativeFee',
    label: VDRIVE_NEGATIVE_FEE_LABEL,
    tooltip: VDRIVE_NEGATIVE_FEE_TOOLTIP,
  },
  { key: 'serviceFee', label: SERVICE_FEE_LABEL, tooltip: SERVICE_FEE_TOOLTIP },
  { key: 'withdraw', label: 'vPay Fee' },
];

/** Net Revenue (Total Income) — Pie/Graph tabbed card. */
export const TotalIncomeCard = ({
  incomeSeries,
  incomeSeriesCompared,
  totalIncomeSum,
  totalIncomeSumToCompare,
  groupByType,
  isComparing,
  servicesBreakdown,
  vGrubsProviderBreakdown,
  periodLabel,
  onOpenSaasDetails,
  onOpenServiceFeeDetails,
}: {
  incomeSeries: BaseChartDataItem[];
  incomeSeriesCompared: BaseChartDataItem[];
  totalIncomeSum: number;
  totalIncomeSumToCompare: number;
  groupByType: GroupByType;
  isComparing: boolean;
  servicesBreakdown: FinancesServicesBreakdown | null | undefined;
  /** vGrubs VR split per order provider - opens from that legend row. */
  vGrubsProviderBreakdown?: FinancesVGrubsProviderRow[] | null;
  /** Active date range, shown under the headline. */
  periodLabel?: string;
  /** Opens what Monthly Saas is made of - from that legend row's arrow. */
  onOpenSaasDetails?: () => void;
  /** Opens where the Service Fee came from - from that legend row's arrow. */
  onOpenServiceFeeDetails?: () => void;
}) => {
  // Earnings per provider, with the order count behind each one - it counts
  // the orders `net` was earned on, so it only belongs on this card.
  const vGrubsProviderRows = buildVGrubsProviderRows(
    vGrubsProviderBreakdown,
    'net',
    true,
    periodLabel
  );

  const breakdownItems: BreakdownItem[] = SERVICE_LABELS.map(
    ({ key, label, tooltip }) => ({
      label,
      tooltip,
      amount: servicesBreakdown?.[key] ?? 0,
      color: SERVICE_COLORS[key],
      drilldown:
        key === 'vGrubs' && vGrubsProviderRows.length
          ? { title: 'Net by Provider', rows: vGrubsProviderRows }
          : undefined,
      onOpen:
        key === 'saas'
          ? onOpenSaasDetails
          : key === 'serviceFee'
          ? onOpenServiceFeeDetails
          : undefined,
    })
  );

  return (
    <FinanceTabbedCard
      title="Net Revenue"
      headlineCents={totalIncomeSum}
      headlineCompareCents={totalIncomeSumToCompare}
      valueColor={lightTheme.green}
      series={incomeSeries}
      seriesCompared={incomeSeriesCompared}
      seriesKey="totalIncome"
      tooltipLabel="Total Income"
      breakdownItems={breakdownItems}
      breakdownAmountLabel="Income"
      // Six services, all of which add up to the headline - keep them on the
      // card rather than sending the last one behind "View all".
      breakdownLegendLimit={SERVICE_LABELS.length}
      periodLabel={periodLabel}
      groupByType={groupByType}
      isComparing={isComparing}
    />
  );
};
